import { supabase } from "../clients/supabaseClient";

type ImportResult = { success: number; failed: number; errors: string[] };

const BUCKET_NAME = "product_images";
const CONCURRENCY_LIMIT = 5;

function isValidImageFile(file: File): boolean {
    const isImage = file.type.startsWith("image/");
    const name = file.name.toLowerCase();
    const isSystem =
        name.startsWith(".") ||
        name === "thumbs.db" ||
        (file.webkitRelativePath && file.webkitRelativePath.includes("__MACOSX"));

    return isImage && !isSystem;
}

function getFileName(file: File): string {
    return file.webkitRelativePath
        ? file.webkitRelativePath.split(/[\\/]/).pop()!
        : file.name;
}

function getSkuFromFileName(fileName: string): string {
    return fileName.split(".")[0].split("_")[0].trim();
}

function getFileExtension(fileName: string): string {
    const parts = fileName.split(".");
    return parts.length > 1 ? parts.pop()!.toLowerCase() : "";
}

function getStoragePathFromPublicUrl(publicUrl: string): string | null {
    try {
        const url = new URL(publicUrl);
        const marker = `/storage/v1/object/public/${BUCKET_NAME}/`;
        const index = url.pathname.indexOf(marker);

        if (index === -1) return null;

        return decodeURIComponent(url.pathname.slice(index + marker.length));
    } catch {
        return null;
    }
}

async function runWithConcurrency<T>(
    items: T[],
    limit: number,
    worker: (item: T, index: number) => Promise<void>
): Promise<void> {
    let nextIndex = 0;

    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (nextIndex < items.length) {
            const currentIndex = nextIndex++;
            await worker(items[currentIndex], currentIndex);
        }
    });

    await Promise.all(runners);
}

export async function importProductImages(
    files: FileList | File[],
    onProgress?: (progress: number) => void
): Promise<ImportResult> {
    const allFiles = Array.from(files).filter(isValidImageFile);

    if (allFiles.length === 0) {
        return { success: 0, failed: 0, errors: [] };
    }

    const errors: string[] = [];
    let success = 0;
    let failed = 0;
    let completed = 0;

    const fileMeta = allFiles.map((file) => {
        const fileName = getFileName(file);
        const sku = getSkuFromFileName(fileName);
        const fileExt = getFileExtension(fileName);

        return { file, fileName, sku, fileExt };
    });

    const skus = [...new Set(fileMeta.map((item) => item.sku).filter(Boolean))];

    const { data: products, error: fetchError } = await supabase
        .from("products")
        .select("id, sku, image_url")
        .in("sku", skus);

    if (fetchError) {
        throw new Error(`Failed to load products: ${fetchError.message}`);
    }

    const productBySku = new Map(
        (products ?? []).map((product) => [product.sku, product])
    );

    await runWithConcurrency(fileMeta, CONCURRENCY_LIMIT, async ({ file, fileName, sku, fileExt }, index) => {
        try {
            const product = productBySku.get(sku);

            if (!product) {
                throw new Error(`SKU "${sku}" not found in database.`);
            }

            if (!fileExt) {
                throw new Error(`File "${fileName}" has no valid extension.`);
            }

            const storagePath = `${sku}-${Date.now()}-${index}.${fileExt}`;

            const { error: uploadError } = await supabase.storage
                .from(BUCKET_NAME)
                .upload(storagePath, file, {
                    upsert: false,
                    contentType: file.type,
                    cacheControl: "3600",
                });

            if (uploadError) {
                throw new Error(`Upload failed: ${uploadError.message}`);
            }

            const { data } = supabase.storage
                .from(BUCKET_NAME)
                .getPublicUrl(storagePath);

            const publicUrl = data.publicUrl;

            const { error: updateError } = await supabase
                .from("products")
                .update({ image_url: publicUrl })
                .eq("id", product.id);

            if (updateError) {
                throw new Error(`Database update failed: ${updateError.message}`);
            }

            if (product.image_url) {
                const oldStoragePath = getStoragePathFromPublicUrl(product.image_url);

                if (oldStoragePath) {
                    await supabase.storage.from(BUCKET_NAME).remove([oldStoragePath]);
                }
            }

            success++;
        } catch (err: any) {
            console.error(err);
            errors.push(`${file.name}: ${err.message}`);
            failed++;
        } finally {
            completed++;
            if (onProgress) {
                onProgress(Math.round((completed / allFiles.length) * 100));
            }
        }
    });

    return { success, failed, errors };
}